import type { AppKey } from '@iac/contracts';
import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { useAuth } from '../lib/auth';

/**
 * These decide what the UI *offers*. They are not the authorization boundary —
 * the API re-checks every request, and a user who edits their way past these
 * still gets 401/403 JSON. Their job is to avoid showing a page that is
 * guaranteed to fail.
 */

function Pending() {
  // Deliberately blank: the session check is a single request against the same
  // origin, and a spinner that flashes for 20ms reads as a glitch.
  return null;
}

export function RequireAuth() {
  const { status } = useAuth();
  if (status === 'loading') return <Pending />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;
  return <Outlet />;
}

export function RequireAnonymous({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return <Pending />;
  if (status === 'authenticated') return <Navigate to="/apps" replace />;
  return <>{children}</>;
}

/**
 * Frame for the catch-all route. Deliberately not behind `RequireAuth`: an
 * unknown URL must not bounce a signed-out visitor to the login page.
 */
export function NotFoundFrame() {
  const { status } = useAuth();
  if (status === 'loading') return <Pending />;
  if (status === 'authenticated') return <AppLayout />;
  return <Outlet />;
}

/** Mirrors `AppAccessGuard`'s fallback: no grant means back to the launcher. */
export function RequireApp({ app }: { app: AppKey }) {
  const { status, hasApp } = useAuth();
  if (status === 'loading') return <Pending />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;
  if (!hasApp(app)) return <Navigate to="/apps" replace />;
  return <Outlet />;
}
